// Services.js
import React, { useState, useEffect } from 'react';
import './Services.css';

const Services = () => {
  const services = [
    {
      id: 1,
      icon: '🚗',
      title: 'Car Wash',
      description:
        'Book premium car wash services near you. Fast, affordable, and professional cleaning at your doorstep.',
      features: ['Doorstep Pickup', 'Eco-Friendly Products', 'Starting @ ₹199'],
      badge: 'Popular',
      color: '#4f46e5',
    },
    {
      id: 2,
      icon: '⚽',
      title: 'Turf Booking',
      description:
        'Find and book the best turf slots near you. Football, cricket, and more at the lowest prices.',
      features: ['Instant Confirmation', 'Verified Turfs', 'Starting @ ₹499/hr'],
      badge: 'Trending',
      color: '#10b981',
    },
    {
      id: 3,
      icon: '🏆',
      title: 'Tournaments',
      description:
        'Join exciting tournaments and compete with the best. Register your team and win big prizes.',
      features: ['Join Cups & Leagues', 'Live Score Tracking', 'Cash Prizes up to ₹50K'],
      badge: 'New',
      color: '#f59e0b',
    },
    {
      id: 4,
      icon: '⚡',
      title: 'Quick Booking',
      description:
        'Book any service in seconds with our smart app. Choose your slot, pay online, and get instant confirmation — no calls, no waiting.',
      features: ['1-Tap Instant Booking', 'Secure Online Payment', '24/7 Customer Support'],
      badge: 'Fast',
      color: '#ef4444',
    },
  ];

  const [currentIndex, setCurrentIndex] = useState(0);
  const [cardsToShow, setCardsToShow] = useState(3);
  const [isPaused, setIsPaused] = useState(false);

  // Handle responsive cards per view
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth <= 640) {
        setCardsToShow(1);
      } else if (window.innerWidth <= 992) {
        setCardsToShow(2);
      } else {
        setCardsToShow(3);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Auto slide logic
  useEffect(() => {
    if (isPaused) return;
    const maxIndex = services.length - cardsToShow;
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev >= maxIndex ? 0 : prev + 1));
    }, 3000);

    return () => clearInterval(interval);
  }, [cardsToShow, isPaused, services.length]);

  const goToSlide = (index) => {
    const maxIndex = services.length - cardsToShow;
    if (index > maxIndex) {
      setCurrentIndex(0);
    } else if (index < 0) {
      setCurrentIndex(maxIndex);
    } else {
      setCurrentIndex(index);
    }
  };

  const nextSlide = () => goToSlide(currentIndex + 1);
  const prevSlide = () => goToSlide(currentIndex - 1);

  const maxIndex = Math.max(0, services.length - cardsToShow);

  return (
    <section className="services-section" id="services">
      <div className="services-container">
        {/* Section Header */}
        <div className="services-header">
          <span className="services-tag">OUR SERVICES</span>
          <h2 className="services-title">
            Everything You Need, <br />
            <span className="services-title-highlight">All in One App</span>
          </h2>
          <p className="services-subtitle">
            Book car washes, turf slots, and tournaments — all from a single app.
            Fast, easy, and reliable.
          </p>
        </div>

        {/* Slider Wrapper */}
        <div
          className="services-slider-wrapper"
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
        >
          {/* Prev Arrow */}
          <button
            className="slider-arrow slider-arrow-left"
            onClick={prevSlide}
            aria-label="Previous"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6"></polyline>
            </svg>
          </button>

          {/* Slider Track */}
          <div className="services-slider-viewport">
            <div
              className="services-slider-track"
              style={{
                transform: `translateX(-${currentIndex * (100 / cardsToShow)}%)`,
                transition: 'transform 0.5s ease-in-out',
              }}
            >
              {services.map((service) => (
                <div
                  key={service.id}
                  className="service-card"
                  style={{
                    flex: `0 0 calc(${100 / cardsToShow}% - ${
                      ((cardsToShow - 1) * 20) / cardsToShow
                    }px)`,
                    maxWidth: `calc(${100 / cardsToShow}% - ${
                      ((cardsToShow - 1) * 20) / cardsToShow
                    }px)`,
                  }}
                >
                  <div className="service-card-inner">
                    {/* Badge */}
                    <span
                      className="service-badge"
                      style={{ backgroundColor: `${service.color}15`, color: service.color }}
                    >
                      {service.badge}
                    </span>

                    {/* Icon */}
                    <div
                      className="service-icon-wrapper"
                      style={{ backgroundColor: `${service.color}15` }}
                    >
                      <span className="service-icon">{service.icon}</span>
                    </div>

                    {/* Title */}
                    <h3 className="service-title">{service.title}</h3>

                    {/* Description */}
                    <p className="service-description">{service.description}</p>

                    {/* Features */}
                    <ul className="service-features">
                      {service.features.map((feature, i) => (
                        <li key={i} className="service-feature-item">
                          <span
                            className="service-feature-dot"
                            style={{ backgroundColor: service.color }}
                          ></span>
                          {feature}
                        </li>
                      ))}
                    </ul>

                    {/* ✅ Book Now button REMOVED */}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Next Arrow */}
          <button
            className="slider-arrow slider-arrow-right"
            onClick={nextSlide}
            aria-label="Next"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="9 18 15 12 9 6"></polyline>
            </svg>
          </button>
        </div>

        {/* Dots Indicator */}
        <div className="slider-dots">
          {Array.from({ length: maxIndex + 1 }).map((_, index) => (
            <button
              key={index}
              className={`slider-dot ${currentIndex === index ? 'active' : ''}`}
              onClick={() => goToSlide(index)}
              aria-label={`Go to slide ${index + 1}`}
            ></button>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Services;